<template>
  <div>
    <v-text-field
      :label="label"
      :readonly="readonly"
      v-model="date"
      type="date"
      @click="
        (e) =>
          e.preventDefault() /* prevent browser date picker from appearing */
      "
      @input="onDateChange"
      :color="textColor"
      @keydown="onInputKeyDown"
      :hint="show_hint ? 'Required' : null"
      persistent-hint
      :rules="
        Array.isArray(rules) ? rules : validate ? [(v) => isValidDate(v)] : []
      "
    >
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
        slot="append"
        v-if="!hidePicker"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            ref="dateMenuIcon"
            v-bind="attrs"
            v-on="on"
            @click="onDateMenuClick"
            color="primary"
          >
            mdi-calendar
          </v-icon>
        </template>
        <v-date-picker
          ref="datePicker"
          scrollable
          v-model="date"
          :readonly="readonly"
          show-current
          @input="onDatePicked"
          picker-date
          :color="dateColor"
        >
          <v-btn
            text
            :color="dateBtnColor"
            @click="onTodayClick"
            :disabled="isToday"
            title="Select today"
          >
            Today
          </v-btn>
          <v-spacer />
          <v-btn
            text
            :color="dateBtnColor"
            @click="onCloseClick"
            title="Hide the date picker"
          >
            Close
          </v-btn>
        </v-date-picker>
      </v-menu>
    </v-text-field>
  </div>
</template>

<script>
import moment from "moment";
import { ref, computed, reactive, toRefs } from "@vue/composition-api";
export default {
  name: "DatePicker",
  emits: ["change"],
  props: {
    initialValue: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: "Date",
    },
    show_hint: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    textColor: {
      type: String,
      default: "primary",
    },
    dateColor: {
      type: String,
      default: "primary",
    },
    dateBtnColor: {
      type: String,
      default: "primary",
    },
    validate: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: null,
    },
    hidePicker: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const dateMenuIcon = ref(null);
    const dateFormat = ref("YYYY-MM-DD");
    try {
      var initialValue = props.initialValue
        ? moment(props.initialValue, dateFormat.value).toDate()
        : null;
    } catch (err) {
      initialValue = null;
    }

    // const m = moment(initialValue);
    let date = ref(initialValue ? formatForDatePicker(initialValue) : null);
    let dateMenu = ref(false);
    const data = reactive({
      dateFormat,
      date,
      dateMenu,
    });
    const dateTime = computed(() => {
      if (date.value) {
        // use moment instead of Date to convert the date string to a date because of this: https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
        return moment(date.value, dateFormat.value).toDate();
      }
      return null;
    });
    const isValidDate = (v) => {
      /*
       first make sure `v` isn't null since new Date(null) equals
        a valid date object with a datetime on: Wed Dec 31 1969 17:00:00
      */
      if (v) {
        const d = new Date(v);
        if (
          Object.prototype.toString.call(d) === "[object Date]" &&
          Number.isInteger(d.getTime())
        ) {
          return true;
        }
      }

      return "Invalid date";
    };
    const isToday = computed(() => {
      const now = new Date();
      return dateTime && dateTime.value
        ? dateTime.value.getMonth() === now.getMonth() &&
            dateTime.value.getDate() === now.getDate() &&
            dateTime.value.getFullYear() === now.getFullYear()
        : false;
    });
    /* Format's date to ISO 8601 format for the vuetify date picker YYY-mm-dd */
    function formatForDatePicker(date) {
      const d = new Date(date);
      let month = "" + (d.getMonth() + 1),
        day = "" + d.getDate(),
        year = d.getFullYear();
      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;
      return [year, month, day].join("-");
    }
    function onChange() {
      emit("change", moment(dateTime.value).format("YYYY-MM-DD"));
    }
    const onInputKeyDown = function (e) {
      if (e && e.keyCode === 32) {
        /* ignore if the space bar is pressed so the browser date/time picker doesn't appear */
        return e.preventDefault();
      }
    };
    function onDateChange() {
      // console.log('onDateChange');
      onChange();
    }
    function onDatePicked() {
      // console.log('onDatePicked');
      dateMenu.value = false;
      onChange();
      if (dateMenuIcon) {
        dateMenuIcon.value.$el.focus();
      }
    }
    function onTodayClick() {
      const now = new Date();
      date.value = formatForDatePicker(now);
      dateMenu.value = false;
      onChange();
    }
    function onCloseClick() {
      dateMenu.value = false;
    }
    function onDateMenuClick() {
      if (!dateMenu.value) {
        // focus on the current selected date
        setTimeout(
          (() => {
            const activeDate = document.getElementsByClassName(
              "v-btn v-btn--active"
            );
            if (activeDate && activeDate.length > 0) {
              activeDate[0].focus();
            }
          }).bind(this),
          400
        );
      }
    }
    function setDateValue(setDate) {
      this.date = setDate;
    }
    return {
      ...toRefs(data),
      onDateChange,
      onDatePicked,
      onTodayClick,
      onDateMenuClick,
      onCloseClick,
      onInputKeyDown,
      isToday,
      dateMenuIcon,
      isValidDate,
      setDateValue,
    };
  },
};
</script>
<style lang="scss">
input[type="date"]::-webkit-inner-spin-button,
input[type="date"]::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}

.v-date-picker-table .v-btn.v-btn--active.theme--dark {
  color: #1e1e1e;
}
.v-menu__content.theme--dark .v-picker__title {
  color: #1e1e1e;
}

.v-date-picker-table .v-btn.v-btn--active.theme--light {
  color: white;
}
.v-menu__content.theme--light .v-picker__title {
  color: white;
}
</style>
